import { useState } from "react";

import { useKorrigiereBelegnummer } from "../../hooks/useInvoices";
import { errorText, type InvoiceRegisterItem } from "../../lib/invoices";

type Props = {
  item: InvoiceRegisterItem;
  onUpdated: (item: InvoiceRegisterItem) => void;
};

/**
 * Die Belegnummer richtigstellen, solange der Beleg noch im Haus ist.
 *
 * Der Fall: Kontor vergibt RE0175, und erst danach fällt auf, dass Lexware
 * dieselbe Nummer schon an einen anderen Kunden geschickt hat. Solange beide
 * Programme laufen, ist das kein Ausrutscher, sondern ein wiederkehrender
 * Vorgang — er gehört in die App und nicht in einen Eingriff am Register.
 *
 * Nur für Belege, die Kontor selbst geschrieben hat: eine Registerzeile aus
 * einem anderen Programm trägt die Nummer ihres Originals. Ob der Beleg schon
 * heraus ist (versendet, bezahlt, an einer Bankbuchung, als Abschlag
 * verknüpft), entscheidet der Server — hier steht nur der offensichtliche Teil,
 * damit gar nicht erst ein Feld erscheint, das ohnehin abgewiesen würde.
 */
export default function BelegNummerKorrektur({ item, onUpdated }: Props) {
  const korrigieren = useKorrigiereBelegnummer();
  const [offen, setOffen] = useState(false);
  const [nummer, setNummer] = useState(item.nummer);
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState("");

  const inKontorGeschrieben = Boolean(item.web_festgeschrieben_am);
  const herausgegeben = Boolean(item.bezahlt_am || item.kontobewegung_id);
  if (!inKontorGeschrieben || herausgegeben) return null;

  const speichern = async () => {
    setFehler("");
    try {
      const aktualisiert = await korrigieren.mutateAsync({ id: item.id, nummer, grund });
      onUpdated(aktualisiert);
      setOffen(false);
      setGrund("");
    } catch (error) {
      // Die verständliche Meldung steht im `detail` des Servers, nicht in
      // `message` — dort steht nur „HTTP 409".
      setFehler(errorText(error));
    }
  };

  if (!offen) {
    return (
      <button type="button" className="invoice-button secondary" onClick={() => setOffen(true)}>
        Belegnummer korrigieren
      </button>
    );
  }

  return (
    <div className="invoice-nummer-korrektur">
      <p className="invoice-kicker">Belegnummer korrigieren</p>
      <p className="invoice-empty-copy">
        Kontor rendert das Dokument mit der neuen Nummer neu und schreibt die Änderung in den
        Verlauf. Möglich, solange der Beleg nicht versendet, bezahlt oder verknüpft ist.
      </p>
      <label className="invoice-field invoice-field-wide">
        <span>Neue Nummer</span>
        <input
          value={nummer}
          onChange={(event) => setNummer(event.target.value)}
          spellCheck={false}
          autoComplete="off"
        />
      </label>
      <label className="invoice-field invoice-field-wide">
        <span>Grund</span>
        <input
          value={grund}
          onChange={(event) => setGrund(event.target.value)}
          placeholder="z. B. Nummer in Lexware bereits vergeben"
        />
      </label>
      {fehler && <p className="invoice-form-error" role="alert">{fehler}</p>}
      <div className="invoice-nummer-korrektur-knoepfe">
        <button
          type="button"
          className="invoice-button primary"
          onClick={speichern}
          disabled={korrigieren.isPending || !nummer.trim() || nummer.trim() === item.nummer}
        >
          {korrigieren.isPending ? "Wird geändert …" : `Auf ${nummer.trim() || "…"} ändern`}
        </button>
        <button
          type="button"
          className="invoice-button secondary"
          onClick={() => { setOffen(false); setNummer(item.nummer); setFehler(""); }}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}
